/**
 * Notice and lease-agreement document templates — versioned, immutable once
 * published (invariant 8's mechanism, reused here for invariant 7's
 * content). "Publish" always creates a new version; there is no update
 * route, which is what actually enforces immutability (see
 * `createDocumentTemplateVersion` in modules/leases/service.ts).
 *
 * Every kind starts empty in every environment until the firm supplies real
 * template language — the empty state below is the expected, common case,
 * not an error. Once a published template exists for a kind, the matching
 * generator (notice generation, lease agreement generation) stops using its
 * disclosed placeholder and starts merge-filling this content instead.
 */

import { useEffect, useState } from 'react';
import { useInvalidate, useList } from '@refinedev/core';
import {
  DECLINE_NOTICE_MERGE_TOKEN_GROUPS,
  DOCUMENT_TEMPLATE_KINDS,
  LEASE_MERGE_TOKEN_GROUPS,
  NOTICE_MERGE_TOKEN_GROUPS,
  TRANSACTIONAL_EMAIL_TEMPLATE_KINDS,
  TRANSACTIONAL_EMAIL_TOKEN_GROUPS,
  type DocumentTemplateDto,
  type DocumentTemplateKind,
} from 'shared';
import { api, ApiError } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';
import { Field, FormErrors, Select, TextArea, humanizeEnum } from '../../../components/Form.js';
import { Lightbox } from '../../../components/Lightbox.js';
import { RichTextEditor } from '../../../components/RichTextEditor.js';
import { SAMPLE_BODY_TEMPLATES } from './sample-body-templates.js';

/**
 * Owner statement tokens (owner.names, owner.mailingAddress, statement.*)
 * aren't in NOTICE_MERGE_TOKEN_GROUPS — no dedicated group exists for this
 * kind yet. Only the company.* chips below resolve; the itemized line table
 * and totals are fixed PDF layout the generator draws itself, not part of
 * this free-text body.
 */
const OWNER_STATEMENT_TOKEN_NOTE =
  'Only company.* fields are available to insert here — owner name/address and the statement figures are drawn as fixed layout by the generator, not merge-field text.';

/**
 * Raw fetch, not the `api()` helper — the response here is a PDF binary, not
 * JSON. Same pattern as LeaseWizard.tsx's `fetchPreviewPdf`.
 */
async function fetchTemplatePreviewPdf(payload: unknown): Promise<Blob> {
  const response = await fetch('/api/staff/document-templates/preview', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      // A non-JSON error body means the request never reached Fastify.
    }
    throw new Error(message);
  }
  return response.blob();
}

export function DocumentTemplateList() {
  const invalidate = useInvalidate();
  const canWrite = useCan('document-templates:write');

  const [kind, setKind] = useState<DocumentTemplateKind>(DOCUMENT_TEMPLATE_KINDS[0]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyIsEmpty, setBodyIsEmpty] = useState(true);
  // Tiptap's `content` only seeds the editor's *initial* document — it is
  // not a continuously-synced controlled value — so clearing `body` state
  // after a successful publish doesn't by itself clear the visible editor.
  // Remounting via a changing `key` is the reliable way to reset it.
  const [bodyEditorKey, setBodyEditorKey] = useState(0);
  const [forwardingAddressClause, setForwardingAddressClause] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [emailPreview, setEmailPreview] = useState<{
    subject: string;
    html: string;
    text: string;
  } | null>(null);

  const [testSendBusy, setTestSendBusy] = useState(false);
  const [testSendResult, setTestSendResult] = useState<string | null>(null);
  const [testSendError, setTestSendError] = useState<unknown>(null);

  // Which kind's tile is open in the preview Lightbox, or null — see the
  // grid of tiles below. Kept as just the kind rather than the row itself
  // so a re-publish while the preview happens to be open still resolves to
  // the newest published row on the next render, not a stale snapshot.
  const [previewKind, setPreviewKind] = useState<DocumentTemplateKind | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // A stale email/PDF preview or "sent" confirmation from a different kind
  // reads as confusing rather than useful once the composer moves on.
  useEffect(() => {
    setEmailPreview(null);
    setTestSendResult(null);
    setTestSendError(null);
  }, [kind]);

  const { data, isLoading } = useList<DocumentTemplateDto>({
    resource: 'staff/document-templates',
    pagination: { mode: 'off' },
  });

  const rows = data?.data ?? [];
  // Latest version per kind first — same ordering the API already returns
  // (kind asc, version desc), grouped here only for display.
  const byKind = new Map<string, DocumentTemplateDto[]>();
  for (const row of rows) {
    const list = byKind.get(row.kind) ?? [];
    list.push(row);
    byKind.set(row.kind, list);
  }

  // The version each generator actually uses — latest PUBLISHED, not
  // merely highest version number (see `createDocumentTemplateVersion`'s
  // callers, e.g. `.findFirst({ where: { isPublished: true }, orderBy: {
  // version: 'desc' } })`). Today the composer only ever publishes, so
  // this is normally the same row as `byKind`'s first entry — but reading
  // it this way keeps the tile honest if that ever stops being true.
  const currentByKind = new Map<string, DocumentTemplateDto>();
  for (const [groupKind, versions] of byKind) {
    const published = versions.find((v) => v.isPublished);
    if (published) currentByKind.set(groupKind, published);
  }
  const previewTemplate = previewKind ? (currentByKind.get(previewKind) ?? null) : null;

  const isLeaseAgreement = kind === 'LEASE_AGREEMENT';
  const isTransactionalEmail = TRANSACTIONAL_EMAIL_TEMPLATE_KINDS.includes(kind as never);
  // Neither kind is ever emailed on generation (LEASE_AGREEMENT is stored
  // only; OWNER_STATEMENT goes straight to a Document-free OwnerStatement
  // row — see that model's doc comment), so neither has a subject line.
  // PORTAL_INVITE_EMAIL/PASSWORD_RESET_EMAIL are genuinely emails and
  // always show one, same as every other notice-shaped kind.
  const hasSubject = !isLeaseAgreement && kind !== 'OWNER_STATEMENT';
  const tokenGroups = isLeaseAgreement
    ? LEASE_MERGE_TOKEN_GROUPS
    : isTransactionalEmail
      ? TRANSACTIONAL_EMAIL_TOKEN_GROUPS
      : kind === 'DECLINE_NOTICE'
        ? DECLINE_NOTICE_MERGE_TOKEN_GROUPS
        : NOTICE_MERGE_TOKEN_GROUPS;
  const tokenGroupsNote = kind === 'OWNER_STATEMENT' ? OWNER_STATEMENT_TOKEN_NOTE : undefined;

  // Tiptap's getHTML() on an empty editor returns "<p></p>", not "" — the
  // editor tracks true emptiness itself (bodyIsEmpty) rather than relying on
  // a string check here. Shared by both Publish and Preview: previewing
  // nothing isn't meaningful either.
  const formIsReady =
    !bodyIsEmpty && (!isLeaseAgreement || Boolean(forwardingAddressClause.trim()));

  function buildPayload() {
    return {
      kind,
      subject: subject.trim() || null,
      body,
      forwardingAddressClause: isLeaseAgreement ? forwardingAddressClause.trim() : null,
    };
  }

  /**
   * Seeds the body with real letter/lease-shaped starter content for the
   * selected kind (sample-body-templates.ts) — merge chips already inserted,
   * bracketed placeholders for whatever the firm still has to supply.
   * Confirms before clobbering anything already typed; remounts the editor
   * (same mechanism the post-publish reset uses) since Tiptap's `content`
   * only seeds the *initial* document.
   */
  function loadSampleBody() {
    if (
      !bodyIsEmpty &&
      !window.confirm('Replace the current body with sample starter content for this kind?')
    ) {
      return;
    }
    setBody(SAMPLE_BODY_TEMPLATES[kind]);
    setBodyIsEmpty(false);
    setBodyEditorKey((k) => k + 1);
  }

  async function publish() {
    if (!formIsReady) return;
    setError(null);
    setBusy(true);
    try {
      await api('/staff/document-templates', {
        method: 'POST',
        body: JSON.stringify(buildPayload()),
      });
      setSubject('');
      setBody('');
      setBodyIsEmpty(true);
      setBodyEditorKey((k) => k + 1);
      setForwardingAddressClause('');
      setEmailPreview(null);
      setTestSendResult(null);
      await invalidate({ resource: 'staff/document-templates', invalidates: ['list'] });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function preview() {
    if (!formIsReady) return;
    setPreviewError(null);
    setPreviewBusy(true);
    try {
      if (isTransactionalEmail) {
        const result = await api<{ subject: string; html: string; text: string }>(
          '/staff/document-templates/preview',
          { method: 'POST', body: JSON.stringify(buildPayload()) },
        );
        setEmailPreview(result);
      } else {
        const blob = await fetchTemplatePreviewPdf(buildPayload());
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(blob));
      }
    } catch (caught) {
      setPreviewError(caught);
    } finally {
      setPreviewBusy(false);
    }
  }

  async function sendTestEmail() {
    if (!formIsReady) return;
    setTestSendError(null);
    setTestSendBusy(true);
    try {
      const { sentTo } = await api<{ sentTo: string }>(
        '/staff/document-templates/send-test-email',
        {
          method: 'POST',
          body: JSON.stringify(buildPayload()),
        },
      );
      setTestSendResult(sentTo);
    } catch (caught) {
      setTestSendError(caught);
    } finally {
      setTestSendBusy(false);
    }
  }

  return (
    <section className="stack">
      <h1>Document Templates</h1>
      <p className="muted">
        Firm-authored content merged into generated notices and lease agreements — see Company
        Information for the fields available as <code>{'{{company.*}}'}</code> tokens. Publishing a
        new version never changes what an already-generated document says (invariant 8): every
        generated document points at the exact version used.
      </p>

      {isLoading ? <p className="muted">Loading…</p> : null}

      {currentByKind.size === 0 && !isLoading ? (
        <p className="muted">Nothing published yet.</p>
      ) : (
        <div className="doc-template-grid">
          {[...currentByKind.entries()].map(([groupKind, current]) => {
            const versionCount = byKind.get(groupKind)?.length ?? 1;
            return (
              <button
                key={groupKind}
                type="button"
                className="doc-template-tile"
                onClick={() => setPreviewKind(groupKind as DocumentTemplateKind)}
              >
                <span className="row" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <span className="doc-template-tile__kind">{humanizeEnum(groupKind)}</span>
                  <span className="pill">v{current.version}</span>
                </span>
                <span className="doc-template-tile__subject">
                  {current.subject ?? '(no subject)'}
                </span>
                {versionCount > 1 ? (
                  <span className="muted" style={{ fontSize: '0.8rem' }}>
                    {versionCount} versions published
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {canWrite ? (
        <div className="card stack">
          <h2>Publish a version</h2>
          <p className="muted">
            Publishing always adds a new version for the selected kind — there is no way to edit one
            in place.
          </p>
          {error ? <FormErrors error={error} /> : null}

          <Field label="Kind" name="kind">
            <Select
              name="kind"
              value={kind}
              onChange={(value) => setKind(value as DocumentTemplateKind)}
              options={DOCUMENT_TEMPLATE_KINDS}
            />
          </Field>

          {hasSubject ? (
            <Field
              label="Email subject"
              name="subject"
              hint="Merge fields like {{unit.label}} apply here too."
            >
              <TextArea name="subject" rows={1} value={subject} onChange={setSubject} />
            </Field>
          ) : null}

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="button button--quiet" onClick={loadSampleBody}>
              Load sample content
            </button>
          </div>

          <Field
            label="Body"
            name="body"
            hint="Use the toolbar to format text and insert merge fields — no need to type {{tokens}} by hand."
          >
            <RichTextEditor
              key={bodyEditorKey}
              value={body}
              onChange={setBody}
              onEmptyChange={setBodyIsEmpty}
              tokenGroups={tokenGroups}
              {...(tokenGroupsNote ? { tokenGroupsNote } : {})}
            />
          </Field>
          <p className="muted">
            "Load sample content" fills in starter language shaped like a real{' '}
            {humanizeEnum(kind).toLowerCase()} — bracketed <code>[placeholders]</code> and merge
            fields still need the firm's actual language and counsel review before this is
            published.
          </p>

          {isLeaseAgreement ? (
            <Field
              label="Forwarding-address clause"
              name="forwardingAddressClause"
              required
              hint="MCL 554.603(2) — always rendered bold, at least 12pt and four sizes larger than the lease body, regardless of this clause's own text formatting."
            >
              <TextArea
                name="forwardingAddressClause"
                rows={4}
                value={forwardingAddressClause}
                onChange={setForwardingAddressClause}
              />
            </Field>
          ) : null}

          {previewError ? <FormErrors error={previewError} /> : null}
          {testSendError ? (
            <p className="alert alert--error">
              {testSendError instanceof ApiError
                ? testSendError.message
                : 'Could not send the test email.'}
            </p>
          ) : null}

          <div className="row">
            <button
              type="button"
              className="button button--quiet"
              disabled={previewBusy || !formIsReady}
              onClick={() => void preview()}
            >
              {previewBusy ? 'Generating…' : 'Preview'}
            </button>
            {isTransactionalEmail ? (
              <button
                type="button"
                className="button button--quiet"
                disabled={testSendBusy || !formIsReady}
                onClick={() => void sendTestEmail()}
              >
                {testSendResult
                  ? `Sent to ${testSendResult}`
                  : testSendBusy
                    ? 'Sending…'
                    : 'Send test email to myself'}
              </button>
            ) : null}
            <button
              type="button"
              className="button"
              disabled={busy || !formIsReady}
              onClick={() => void publish()}
            >
              {busy ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </div>
      ) : null}

      {previewUrl ? (
        <Lightbox title="Template preview" onClose={() => setPreviewUrl(null)}>
          <iframe
            title="Template preview"
            src={previewUrl}
            className="document-template-preview-frame"
          />
        </Lightbox>
      ) : null}

      {emailPreview ? (
        <Lightbox title="Email preview" onClose={() => setEmailPreview(null)}>
          <p>
            <strong>Subject:</strong> {emailPreview.subject}
          </p>
          <iframe
            title="Email preview"
            srcDoc={emailPreview.html}
            className="document-template-preview-frame"
          />
        </Lightbox>
      ) : null}

      {previewTemplate ? (
        <Lightbox
          title={`${humanizeEnum(previewTemplate.kind)} — v${previewTemplate.version}`}
          onClose={() => setPreviewKind(null)}
        >
          {previewTemplate.subject ? (
            <p>
              <strong>Subject:</strong> {previewTemplate.subject}
            </p>
          ) : null}
          {/* Read-only render of the stored, already-sanitized body (see
              html-sanitize.ts) — lets staff see the actual formatting of the
              current version rather than a raw string dump. Authorship is
              restricted to document-templates:write (ADMIN only); reading
              this preview is not. */}
          <div
            className="rich-text-editor__preview"
            dangerouslySetInnerHTML={{ __html: previewTemplate.body }}
          />
        </Lightbox>
      ) : null}
    </section>
  );
}
